import type { PaginatedListItem } from '../../components/PopularDestinations/PopularDestinations.types';
import type { SanityUUID } from '../../data/sanity/types';
import type { SemboMarket } from '../../utils/markets';
import type { CustomerOrigin } from '../../utils/types';
import type {
  IngredientQl,
  InputBoardBasisQl,
  ItineraryResponseQl,
  RecipeQl,
} from '../smart-search/types';

export type RecipeQlWithIngredients = Omit<RecipeQl, 'ingredients'> & {
  ingredients: IngredientQl[];
};

export type GetUrlProps = {
  recipe: RecipeQl | string;
  customerOrigin: CustomerOrigin;
  landing?: LandingType;
  tripId?: SanityUUID;
  modal?: 'pax';
  market?: SemboMarket;
  hotelFilters?: HotelFilters;
  day?: string;
  panel?: 'calendar';
  extraQueryParams?: Record<string, string>;
  boardBasis?: InputBoardBasisQl[];
  skipSessionPax?: boolean;
  affiliateId?: string | null;
};

export type TripsItineraries = {
  [slug: string]: ItineraryResponseQl | null | undefined;
};

export type UpdatedRecipe = RecipeQl;

export type UpdatedRecipes = {
  [_id: string]: UpdatedRecipe;
};

export type Airports = { [iata: string]: Airport };
export type Harbours = { [iata: string]: Harbour };

export type Airport = { name: string; city: string; polygonId: number };
export type Harbour = { name: string; city: string };

export type RecipeData = RecipeQl & {
  numberOfAdults: number;
  numberOfChildren: number;
  totalNights: number | undefined;
  destinations: string[];
  activities: string[];
  isMultiStop: boolean;
  hasFlight: boolean;
  hasHotel: boolean;
  hasFerry: boolean;
  hasActivity: boolean;
};

export type ProductData = Partial<{
  code: string;
  type: ProductType;
  name: string;
  imageURL: string | null;
  rating: number;
  city: string;
  country: string;
}>;

export type ProductType = 'Ticket' | 'Hotel';

export type Amenities = Record<string, boolean | null>;
export type GuestRatings = Record<string, number | null>;

export type Hotel = {
  HotelCode: string;
  Images: {
    ImageName: string;
    ImageService: string;
  }[];
  MainPolygon: {
    FullName: string;
    Name: string;
    Id: number;
  } | null;
  Name: string;
  FullName: string;
  HotelNameSlug: string;
  HotelMainPolygonSlug: string;
  Destinations?: {
    DestinationName: string;
    DestinationCode: number;
    DestinationUrl: string;
    RegionName: string;
    RegionCode: string;
    CountryName: string;
    CountryUrl: string;
  }[];
  Address?: {
    Address: string | null;
    City: string | null;
    Country: string | null;
    Email: string | null;
    Phone: string | null;
    ZipCode: string | null;
  };
  Description?: {
    Value: string;
  };
  Position?: {
    Latitude: {
      DecimalDegrees: number;
    };
    Longitude: {
      DecimalDegrees: number;
    };
  };
  SemboSunRating?: number;
  TypeOfHotel?: string;
  OpeningParagraph?: { Value?: string };
  Amenities?: Amenities;
  GuestRatings?: GuestRatings;
};

export type OneOf<T> = {
  [K in keyof T]: { [P in K]: T[P] } & Partial<
    Record<Exclude<keyof T, K>, never>
  >;
}[keyof T];

export type PlacesLists = PaginatedListItem[];

export type PaymentMethod = {
  name: string;
  type: string;
  logoUrl: string;
};

export type AdyenPaymentMethodsResponse = {
  paymentMethods: Array<{ name: string; type: string; brands?: string[] }>;
};

export type HotelFilters = {
  isFamilyProperty?: boolean;
  hasPool?: boolean;
  starRating?: number;
};

export enum ResizeMode {
  FitInside = 'FitInside',
  FitOutside = 'FitOutside',
}

export interface SemboImageOptions {
  resizeMode?: ResizeMode;
  width?: number;
  height?: number;
  timeoutSeconds?: number;
  hotelCode?: string;
  shrinkRectangle?: boolean;
}

export enum PriceType {
  Net = 'net',
  Gross = 'gross',
}

export type LandingType =
  | 'room'
  | 'destination'
  | 'build-your-trip'
  | 'car-trip'
  | 'flights'
  | 'overview'
  | 'activities';

export type TicketIngredientQl = IngredientQl & {
  selectedTicketDate?: string;
};

export type RecipeWithSelectedDateQl = RecipeQl & {
  selectedDate?: string;
};
